import React, { useEffect, useRef } from 'react';
import {
  addIndentAfterBrToHtmlString,
  getAnimatedLines,
  getSafeHtml,
  includesParagraphOrDiv,
} from '@/utils/generic';
import { usePageStore } from '@/store/usePageStore';
import { TTextMultilanguage } from '@/types/global';
import * as Styled from './Typography.styled';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { usePathname } from 'next/navigation';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { usePrevious } from '@/hooks/usePrevious';
import { useIsMobile } from '@/hooks/useIsMobile';

gsap.registerPlugin(ScrollTrigger);

export type TTypographySize =
  | 'default'
  | 'xs'
  | 'sm'
  | 'base'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl';
export type TTypographyTag =
  | 'span'
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'div';
export type TTypographyColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'white';
export type TTypographyLineHeight = 'default' | 'lg' | 'base' | 'sm';
export type TTypographyFontFamily =
  | 'default'
  | 'helvetica-regular'
  | 'helvetica-light'
  | 'gt-america-regular';

export type TTypographyAnimationTypes =
  | 'revealUp'
  | 'fadeInAndUp'
  | 'none'
  | 'clipTextUp';
export interface ITypographyAnimation {
  type?: TTypographyAnimationTypes;
  delay?: number;
  duration?: number;
  initialOpacity?: number;
}

export interface IText {
  text?: string | TTextMultilanguage;
  className?: string;
  size?: TTypographySize;
  sizeMobile?: TTypographySize;
  color?: TTypographyColor;
  lineHeight?: TTypographyLineHeight;
  fontFamily?: TTypographyFontFamily;
  isLoader?: boolean;
  animation?: ITypographyAnimation;
  hideDesktopBrInMobile?: boolean;
  isInTwoColumns?: boolean;
  children?: React.ReactNode;
}

interface ITypography extends IText {
  as?: TTypographyTag;
  addIndentAfterLineBreak?: boolean;
}
export const Typography: React.FC<ITypography> = ({
  as = 'p',
  className,
  text = '',
  size = 'default',
  sizeMobile = 'default',
  color = 'default',
  lineHeight = 'default',
  fontFamily = 'default',
  isLoader = false,
  animation = { type: 'none' },
  isInTwoColumns,
  hideDesktopBrInMobile = true,
  addIndentAfterLineBreak = false,
  ...props
}) => {
  const pathname = usePathname();
  const isSmootherReady = usePageStore((state) => state.isSmootherReady);
  const locale = usePageStore((s) => s.locale);
  const isMobileMenuOpen = usePageStore((s) => s.isMobileMenuOpen);
  const prevIsMobileMenuOpen = usePrevious(isMobileMenuOpen);
  const refTextAnimatedLines = useRef<HTMLDivElement>(null);
  const refTextAnimatedLinesDesktop = useRef<HTMLDivElement>(null);
  const refTypography = useRef<HTMLElement>(null);
  // const prevPathname = usePrevious(pathname);
  // const hasNavigated = prevPathname !== pathname;
  const isMobile = useIsMobile();
  const shouldAddDelay = prevIsMobileMenuOpen && !isMobileMenuOpen;
  const conditionalDelay = shouldAddDelay ? 0.3 : 0;

  const textToUse =
    text && typeof text === 'object' && locale in text
      ? text[locale as keyof TTextMultilanguage] || ''
      : text;

  const textFormated = addIndentAfterLineBreak
    ? addIndentAfterBrToHtmlString((textToUse ?? '') as string)
    : ((textToUse ?? '') as string);

  const isHeadingTag = (as: TTypographyTag): boolean => {
    return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(as);
  };

  const animatedLines =
    animation.type === 'revealUp'
      ? getAnimatedLines({
          input: textFormated,
          mobileSize: sizeMobile || size,
          desktopSize: size,
          isInTwoColumns: isInTwoColumns,
        })
      : undefined;

  // refs
  const hasAnimatedRef = useRef(false);

  const { contextSafe } = useGSAP();

  /* Firefox SecurityError in x-origin iframes — ignore */  
  // const scrollTriggerRefresh = () => {
  //   try { ScrollTrigger.refresh(); } catch {} 
  // };

useEffect(() => {
  if (typeof window === 'undefined') return;
  const root = refTypography.current;
  if (!root) return;

  const emailData = { user: "estudio", userCV: "cv", domain: "bergagonzalez.com" };
  const tel = "+34933187513"; // no spaces

  const onClick = (e: MouseEvent) => {
    const target = (e.target as Element | null)?.closest('.footer-contact, .footer-contact-num, .footer-contact-cv');
    if (!target || !root.contains(target)) return;

    e.preventDefault();

    if (target.classList.contains('footer-contact')) {
      window.location.href = `mailto:${emailData.user}@${emailData.domain}`;
    } else if (target.classList.contains('footer-contact-cv')) {
      window.location.href = `mailto:${emailData.userCV}@${emailData.domain}?subject=Portfolio`;
    } else {
      window.location.href = `tel:${tel}`;
    }
  };

  root.addEventListener('click', onClick);
  return () => root.removeEventListener('click', onClick); // important for StrictMode
}, []);

  const initAnimation = contextSafe(() => {
    if (!isSmootherReady || hasAnimatedRef.current) return;

    const ctx = gsap.context(() => {
      if (animation.type === 'revealUp' && refTextAnimatedLines.current) {
        const lines =
          !isMobile
            ? refTextAnimatedLinesDesktop.current?.querySelectorAll('.animated-line') || []
            : refTextAnimatedLines.current?.querySelectorAll('.animated-line-mobile') || [];

        const numLines = lines.length;

        // keep animation always the same length
        const baseDuration = 1.25;
        const baseStagger = 0.1;
        const baseLines = 5;
        let totalDuration = baseDuration + (baseLines - 1) * baseStagger;

        if (numLines === 1) totalDuration = 1.4;

        const actualStagger = numLines > 1 ? (totalDuration - baseDuration) / (numLines - 1) : 0;

        gsap.set(lines, { clipPath: 'inset(0 0 100% 0)', opacity: 0, y: 30 });

        gsap.to(lines, {
          clipPath: 'inset(0 0 0% 0)',
          duration: 1.25,
          stagger: actualStagger,
          opacity: 1,
          ease: 'power4.out',
          delay: conditionalDelay,
          y: 0,
          scrollTrigger: {
            trigger: refTextAnimatedLines.current,
            start: 'bottom bottom',
            toggleActions: 'play none none none',
            // optional: ensure it never runs a second time even if ST gets recreated
            once: true, // harmless if already defaulted by your setup
          },
        });
      } else if (animation.type === 'fadeInAndUp' && refTypography.current) {
        gsap.set(refTypography.current, { opacity: 0, y: 30 });

        gsap.to(refTypography.current, {
          duration: 1.5,
          opacity: 1,
          ease: 'power2.out',
          y: 0,
          delay: conditionalDelay,
          scrollTrigger: {
            trigger: refTypography.current,
            start: 'bottom bottom',
            toggleActions: 'play none none none',
            once: true,
          },
        });
      }
    });

    hasAnimatedRef.current = true;

    // clean up on route change/unmount only (not on every menu toggle)
    return () => ctx.revert();
  });

  // Only call the initializer when it’s appropriate, and only once
  useEffect(() => {
    if (!isSmootherReady) return;

    // Desktop: init immediately (no menu covering it)
    if (!isMobile && !hasAnimatedRef.current) {
      initAnimation();
      // ScrollTrigger might need a layout pass
      // scrollTriggerRefresh();
    }
  }, [isSmootherReady, isMobile, initAnimation]);

  useEffect(() => {
    if (!isSmootherReady) return;

    // Mobile: wait until the menu closes the first time
    if (isMobile && !isMobileMenuOpen && !hasAnimatedRef.current) {
      initAnimation();
      // after the menu closes, recalc positions
      // scrollTriggerRefresh();
    }
  }, [isMobileMenuOpen, isMobile, isSmootherReady, initAnimation]);

  // to run once per-page, re-arm the flag on pathname changes
  useEffect(() => {
    hasAnimatedRef.current = false;
    // Optionally kill all triggers in your scope here if your route fully remounts
    // ScrollTrigger.getAll().forEach(t => t.kill());
  }, [pathname]);

  return textFormated && !animatedLines ? (
    <Styled.Typography
      className={`${className ?? ''} typography ${animatedLines ? 'animated-line' : ''} ${addIndentAfterLineBreak ? 'has-indents' : ''}`}
      as={includesParagraphOrDiv(textFormated) ? 'div' : as}
      ref={refTypography}
      size={size}
      sizeMobile={sizeMobile}
      lineHeight={lineHeight}
      color={color}
      isLoader={isLoader}
      hideDesktopBrInMobile={hideDesktopBrInMobile}
      fontFamily={
        isHeadingTag(as) && fontFamily === 'default'
          ? 'helvetica-light'
          : fontFamily
      }
      dangerouslySetInnerHTML={{ __html: getSafeHtml(textFormated) }}
      {...props}
    ></Styled.Typography>
  ) : textFormated &&
    animatedLines &&
    animation.type === 'revealUp' &&
    (animatedLines.animatedLinesDesktop || animatedLines.animatedLines) ? (
    <>
      {
        // animated lines for desktop
        <Styled.Typography
          ref={refTextAnimatedLinesDesktop}
          className={`${className ?? ''} typography`}
          as={includesParagraphOrDiv(textFormated) ? 'div' : as}
          size={size}
          data-includes-span
          sizeMobile={sizeMobile}
          data-animation-type={animation.type}
          lineHeight={lineHeight}
          color={color}
          isLoader={isLoader}
          hideDesktopBrInMobile={hideDesktopBrInMobile}
          fontFamily={
            isHeadingTag(as) && fontFamily === 'default'
              ? 'helvetica-light'
              : fontFamily
          }
          {...props}
        >
          {(
            animatedLines?.animatedLinesDesktop || animatedLines?.animatedLines
          )?.map((line, index) => (
            <span
              className="animated-line"
              key={`animated-${index}`}
              dangerouslySetInnerHTML={{ __html: getSafeHtml(line) }}
            ></span>
          ))}
        </Styled.Typography>
      }
      {
        // animated lines for mobile
        <Styled.Typography
          ref={refTextAnimatedLines}
          className={`${className ?? ''} typography ${'display-only-mobile'}`}
          as={includesParagraphOrDiv(textFormated) ? 'div' : as}
          size={size}
          data-includes-span
          data-size={size}
          data-size-mobile={sizeMobile || 'unset'}
          sizeMobile={sizeMobile}
          data-animation-type={animation.type}
          lineHeight={lineHeight}
          color={color}
          isLoader={isLoader}
          hideDesktopBrInMobile={hideDesktopBrInMobile}
          fontFamily={
            isHeadingTag(as) && fontFamily === 'default'
              ? 'helvetica-light'
              : fontFamily
          }
          {...props}
        >
          {animatedLines?.animatedLinesMobile?.map((line, index) => (
            <span
              className="animated-line-mobile"
              key={`animated-mobile-${index}`}
              dangerouslySetInnerHTML={{ __html: getSafeHtml(line) }}
            ></span>
          ))}
        </Styled.Typography>
      }
    </>
  ) : null;
};
